import { useState, useEffect, useRef } from 'react'
import { AllStatusWrapper } from '../../../../components/execution/terminal/AllStatusWrapper'
import {
  AutoPayMandateDetails,
  AutoPaySetupStatus,
  MandateDetailsResponse,
  PaymentStatus,
  TerminalStatus,
  TerminalStatusContext,
} from '../../../../types/mf-execution.types'
import { useSystematicPaymentStatusQuery } from '../../../../hooks/mf-execution/useSystematicPaymentStatusQuery'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { postCTA, upiPay } from '../../../../utils/appBridge'
import { getOrdersTabCTA } from '../../../../utils/mutualFundDetail'
import { convertMillisecondsToMinutesAndSeconds } from '../../../../utils/utils'
import { useBottomSheet } from '../../../../state-management/contexts/BottomSheetContext'
import { PaymentNotCompletedBottomSheet } from '../../../../components/BottomSheetComponents/mf-execution/PaymentNotCompletedBottomSheet'
import { UpiIntentRedirectModal } from '../../../../components/execution/upi/UpiIntentRedirectModal'
import { isUpiIntentsFlowEnabled } from '../../../../utils/mf-execution/upiIntentsFeature'
import { fetchMandateDetails, setupAutoPay } from '../../../../repository/mfExecution'
import {
  buildAutoPayLinkWaitingPath,
  buildAutoPayStatusSearchParams,
  buildAutopayMethodSelectionFromMatchingMandate,
  buildSetupAutoPayPayloadFromMatchingMandate,
} from '../../../../utils/mf-execution/autopayMethodSelection'

/**
 * SIP first-payment status (UPI / UPI intent).
 * On success: legacy apps → v1 AutoPayMethodsPage; UPI-intent apps → mandate-details
 * decides between setupAutoPay + autopay status/link-waiting vs v2 SetupAutoPay.
 */
const PAYMENT_SUCCESS_NAVIGATION_DELAY_MS = 2000

export const SIPUPIPaymentStatusPage = () => {
  const [status, setStatus] = useState<TerminalStatus>(TerminalStatus.AWAITING)
  const upiIntentsEnabled = isUpiIntentsFlowEnabled()
  const [showUpiRedirectModal, setShowUpiRedirectModal] = useState(false)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()
  const hasTriggeredUpiIntentRef = useRef(false)
  /** Prevents duplicate mandate-details fetch + navigation if the success effect re-runs. */
  const hasHandledPaymentSuccessRef = useRef(false)
  const sxpId = searchParams.get('sxpId')
  const displayAccNumber = searchParams.get('displayAccNumber') || 'your bank account'

  const { paymentStatus, isError, isExpired, timeLeft } = useSystematicPaymentStatusQuery({ maxTimer: 120000 })

  const isUpiIntentAwaiting =
    upiIntentsEnabled &&
    paymentStatus?.type === 'UPI_INTENT' &&
    paymentStatus?.status === PaymentStatus.AWAITING &&
    !isError

  useEffect(() => {
    hasTriggeredUpiIntentRef.current = false
    hasHandledPaymentSuccessRef.current = false
    setShowUpiRedirectModal(false)
  }, [sxpId])

  useEffect(() => {
    const actionUrl = paymentStatus?.actionUrl

    if (!isUpiIntentAwaiting || !actionUrl || hasTriggeredUpiIntentRef.current) {
      if (!isUpiIntentAwaiting) {
        setShowUpiRedirectModal(false)
      }
      return
    }

    setShowUpiRedirectModal(true)

    const redirectTimeoutId = window.setTimeout(() => {
      hasTriggeredUpiIntentRef.current = true
      upiPay({
        upiLink: actionUrl,
        packageName: paymentStatus?.upiPackageName,
      })
      setShowUpiRedirectModal(false)
    }, 1000)

    return () => window.clearTimeout(redirectTimeoutId)
  }, [isUpiIntentAwaiting, paymentStatus?.actionUrl, paymentStatus?.upiPackageName])

  const handleBackButtonClick = () => {
    openBottomSheet(
      <PaymentNotCompletedBottomSheet
        onDismiss={() => {
          closeBottomSheet()
        }}
        onStayOnScreen={() => {
          closeBottomSheet()
        }}
        onExitAnyway={() => {
          closeBottomSheet()
          postCTA(JSON.stringify(getOrdersTabCTA()))
        }}
        ScreenName='SIPUPIPaymentStatusPage'
      />
    )
  }

  useEffect(() => {
    if (isError || paymentStatus?.status === PaymentStatus.FAILED) {
      setStatus(TerminalStatus.FAILURE)
      return
    }

    if (paymentStatus?.status === PaymentStatus.AWAITING) {
      setStatus(TerminalStatus.AWAITING)
      return
    }

    if (paymentStatus?.status !== PaymentStatus.SUCCESS) {
      return
    }

    if (hasHandledPaymentSuccessRef.current) {
      return
    }
    hasHandledPaymentSuccessRef.current = true

    setStatus(TerminalStatus.PARTIAL_SUCCESS)

    const navigationTimeoutId = window.setTimeout(() => {
      void navigateAfterSipPaymentSuccess({
        sxpId,
        upiIntentsEnabled,
        upiPackageName: paymentStatus?.upiPackageName,
        navigate,
      })
    }, PAYMENT_SUCCESS_NAVIGATION_DELAY_MS)

    return () => window.clearTimeout(navigationTimeoutId)
  }, [paymentStatus, isError, sxpId, upiIntentsEnabled, navigate])

  useEffect(() => {
    if (isExpired && (status === TerminalStatus.LOADING || status === TerminalStatus.AWAITING)) {
      setStatus(TerminalStatus.PENDING)
    }
  }, [isExpired, status])

  return (
    <div className='w-full h-screen relative overflow-hidden'>
      {upiIntentsEnabled && showUpiRedirectModal ? (
        <UpiIntentRedirectModal
          upiPackageName={paymentStatus?.upiPackageName}
          actionUrl={paymentStatus?.actionUrl}
        />
      ) : null}
      <AllStatusWrapper
        status={status}
        statusContext={TerminalStatusContext.SIP}
        awaitingConfig={{

          subtitle: upiIntentsEnabled ? `Complete payment using ${displayAccNumber}. <br/ > This may take a few seconds.` : '',
          title: upiIntentsEnabled
            ? `Confirming your <br /> payment status`
            : `Go to your UPI app and complete payment using ${displayAccNumber}`,
          timerText: convertMillisecondsToMinutesAndSeconds(timeLeft),
          caption: 'Do not close this screen',
          onBackButtonClick: handleBackButtonClick
        }}
        partialSuccessConfig={{
          title: 'Payment Completed'
        }}
        failureConfig={{
          title: 'Payment failed',
          caption: 'Any amount deducted will be credited to <br /> source within 2-4 working days.',
          ctas: [
            {
              ctaType: 'primary',
              label: 'Close',
              onClick: () => {
                // Handle navigation to dashboard
                console.log('Navigate to dashboard')
                postCTA(JSON.stringify(getOrdersTabCTA()))
              }
            }
          ]
        }}
        pendingConfig={{
          title: 'Payment pending',
          caption: 'It’s taking longer than expected. We’ll <br /> notify you once your payment is confirmed.',
          ctas: [
            {
              ctaType: 'primary',
              label: 'Close',
              onClick: () => {
                // Handle navigation to dashboard
                console.log('Navigate to dashboard')
                postCTA(JSON.stringify(getOrdersTabCTA()))
              }
            }
          ]
        }}
      />
    </div>
  )
}

type NavigateAfterSipPaymentSuccessArgs = {
  sxpId: string | null
  upiIntentsEnabled: boolean
  upiPackageName?: string | null
  navigate: ReturnType<typeof useNavigate>
}

/**
 * Routes the user after the first SIP UPI payment succeeds.
 *
 * Legacy apps (app version below UPI intents threshold): unchanged flow — always open
 * the v1 bank-account picker (`AutoPayMethodsPage`).
 *
 * UPI intents apps: call `fetchMandateDetails` and branch on whether the SIP's required
 * mandate type already exists in `availableMandates`:
 * - Match found → POST `setupAutoPay` to register the mandate with execution, then navigate
 *   to autopay status (SUCCESS) or link-waiting (INITIATED) — same as `SetupAutoPay`.
 * - No match → open `SetupAutoPay` so the user can configure autopay for the required type.
 */
async function navigateAfterSipPaymentSuccess({
  sxpId,
  upiIntentsEnabled,
  upiPackageName,
  navigate,
}: NavigateAfterSipPaymentSuccessArgs) {
  if (!sxpId) {
    return
  }

  if (!upiIntentsEnabled) {
    navigate(`/sip/auto-pay/methods?sxpId=${sxpId}`)
    return
  }

  try {
    const mandateDetails = await fetchMandateDetails(sxpId)
    const matchingMandate = findAvailableMandateMatchingRequiredType(mandateDetails)

    if (matchingMandate?.mandateId) {
      const setupPayload = buildSetupAutoPayPayloadFromMatchingMandate({
        sxpId,
        matchingMandate,
        mandateDetails,
      })

      if (!setupPayload?.accountId) {
        navigate(buildSetupAutoPayPath(sxpId, mandateDetails))
        return
      }

      const autopayMethod = buildAutopayMethodSelectionFromMatchingMandate(
        matchingMandate,
        mandateDetails,
        upiPackageName
      )

      const statusParams = buildAutoPayStatusSearchParams({
        sxpId,
        mandateId: matchingMandate.mandateId ?? '',
        autopayMethod,
      })

      try {
        const setupResponse = await setupAutoPay(setupPayload)
        const resolvedStatusParams = buildAutoPayStatusSearchParams({
          sxpId: setupResponse?.sxpId ?? sxpId,
          mandateId: setupResponse?.mandateId ?? matchingMandate.mandateId ?? '',
          autopayMethod,
        })

        if (setupResponse?.status === AutoPaySetupStatus.SUCCESS) {
          navigate(`/sip/auto-pay/status?${resolvedStatusParams.toString()}`)
          return
        }

        if (setupResponse?.status === AutoPaySetupStatus.INITIATED) {
          navigate(
            buildAutoPayLinkWaitingPath({
              sxpId: setupResponse?.sxpId ?? sxpId,
              mandateId: setupResponse?.mandateId ?? matchingMandate.mandateId ?? '',
              autopayMethod,
            })
          )
          return
        }
      } catch {
        navigate(`/sip/auto-pay/status?${statusParams.toString()}`)
        return
      }

      navigate(buildSetupAutoPayPath(sxpId, mandateDetails))
      return
    }

    navigate(buildSetupAutoPayPath(sxpId, mandateDetails))
  } catch {
    // Mandate-details is best-effort routing input; if it fails, still land on setup.
    navigate(`/v2/sip/auto-pay/methods?sxpId=${sxpId}`)
  }
}

/**
 * Returns the first `availableMandates` row whose `mandateType` matches the SIP-level
 * `mandateType` from mandate-details. A row must have a non-null `mandateId` so we can
 * pass it to the autopay status screen.
 */
export function findAvailableMandateMatchingRequiredType(
  mandateDetails: MandateDetailsResponse | null | undefined
): AutoPayMandateDetails | null {
  const requiredMandateType = mandateDetails?.mandateType
  const availableMandates = mandateDetails?.availableMandates ?? []

  if (!requiredMandateType) {
    return null
  }

  return (
    availableMandates.find(
      (mandate) =>
        mandate?.mandateType === requiredMandateType && mandate?.mandateId != null && mandate.mandateId !== ''
    ) ?? null
  )
}

/** Builds the v2 setup screen URL, forwarding sip date from mandate-details when present. */
export function buildSetupAutoPayPath(sxpId: string, mandateDetails: MandateDetailsResponse | null | undefined) {
  const params = new URLSearchParams({ sxpId })

  if (mandateDetails?.sipDate != null && Number.isFinite(mandateDetails.sipDate)) {
    params.set('sipDate', String(mandateDetails.sipDate))
  }

  return `/v2/sip/auto-pay/methods?${params.toString()}`
}
